import React, { useCallback, useEffect, useState } from "react";
import { Settings, Info, Eye } from "lucide-react";
import { Link, useOutletContext } from "react-router-dom";
import { useSectionContext, SectionKey } from "../../context/SectionContext";
import {
  updateSectionStatus,
  getWebsiteSettings,
} from "../../services/websitesetting";
import ToggleSetting from "../../components/ui/ToggleSetting";
import SectionHeader from "../../components/ui/SectionHeader";
import { Button } from "../../components/ui/Button";
import ViewWebsiteButton from "../../components/ui/ViewWebsiteButton";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { useToast } from "../../hooks/useToast";
import InfoContainer from "../../components/ui/InfoContainer";
import WebsiteSectionCard from "../../components/ui/WebsiteSectionCard";
import PageHeader from "../../components/ui/PageHeader";

export const sectionIdMap: Record<string, number> = {
  herosection: 1,
  aboutsection: 4,
  servicesection: 2,
  testimonialsection: 3,
  gallerysection: 5,
  footersection: 6,
  fronttheme: 7,
  aboutuspage: 8,
};

const SECTION_NAME_MAP: Record<string, SectionKey> = {
  "Hero Section": "herosection",
  "Service Section": "servicesection",
  "Testimonials Section": "testimonialsection",
  "About Section": "aboutsection",
  "Gallery Section": "gallerysection",
};

const SectionSettings: React.FC = () => {
  const { sections, setSectionState } = useSectionContext();
  const toast = useToast();
  const [localSections, setLocalSections] =
    useState<Record<string, "On" | "Off">>(sections);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const { subdomain } = useOutletContext<{ subdomain: string }>();

  useEffect(() => {
    setLocalSections(sections);
  }, [sections]);

  useEffect(() => {
    const fetchSectionStates = async () => {
      setIsLoading(true);
      try {
        const data = await getWebsiteSettings();
        if (data && Array.isArray(data.sections)) {
          data.sections.forEach((item: any) => {
            const sectionName = item.section?.name;
            const status = item.status === "active" ? "On" : "Off";
            const key = SECTION_NAME_MAP[sectionName];

            if (key) {
              setSectionState(key, status);
            }
          });
        }
      } catch (err) {
        console.error("Failed to fetch section settings:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSectionStates();
  }, []);

  const handleToggleChange = useCallback(
    async (key: keyof typeof sections, val: "On" | "Off") => {
      setLocalSections((prev) => ({ ...prev, [key]: val }));
      setSectionState(key as SectionKey, val);
      const sectionId = sectionIdMap[key];
      if (!sectionId) {
        console.warn(`No ID mapped for section key: ${key}`);
        return;
      }

      try {
        await updateSectionStatus(
          sectionId,
          val === "On" ? "active" : "inactive"
        );
        toast.success(
          `${key.replace("section", "")} Section ${val === "On" ? "enabled" : "disabled"
          } successfully`
        );
        console.log(`Section "${key}" updated to "${val}"`);
      } catch (error: any) {
        setLocalSections((prev) => ({
          ...prev,
          [key]: prev[key] === "On" ? "Off" : "On",
        }));
        if (error.type === "serverValidation") {
          setErrors(error.errors);
          toast.error("Validation failed. Please check your settings.");
        } else {
          console.error(`Failed to update section "${key}"`, error);
          toast.error(`Failed to update ${key} section.`);
        }
      }
    },
    [setSectionState, toast]
  );

  return (
    <>
      {isLoading && (
        <GlobalLoader
          initialPageLoad={true}
          description="Loading section settings..."
        />
      )}
      <div className="space-y-5">
        {/* Header */}
        <PageHeader
          title="Section Settings"
          description="Configure which sections appear on your website"
          variant="card"
          className="mb-5"
          mobileActionsInHeader={true}
          actions={<ViewWebsiteButton subdomain={subdomain} />}
        />

        {/* Main Content */}
        <WebsiteSectionCard title="Website Sections" icon={Settings}>
          <div className="space-y-5">
            {/* Section Toggles */}
            <div>
              <SectionHeader title="Active Sections" />

              {/* Info Box */}
              <InfoContainer
                message="Enable or disable sections to customize your website appearance"
                iconSize={13}
                className="mb-4"
              />

              <div className="space-y-4">
                {/* Hero Section */}
                <ToggleSetting
                  label="Hero Section"
                  description="Main banner section at the top of your website"
                  checked={localSections.herosection === "On"}
                  onChange={(checked) =>
                    handleToggleChange("herosection", checked ? "On" : "Off")
                  }
                />

                {/* Service Section */}
                <ToggleSetting
                  label="Service Section"
                  description="Display your services and offerings"
                  checked={localSections.servicesection === "On"}
                  onChange={(checked) =>
                    handleToggleChange("servicesection", checked ? "On" : "Off")
                  }
                />

                {/* Testimonials Section */}
                <ToggleSetting
                  label="Testimonials Section"
                  description="Show customer reviews and testimonials"
                  checked={localSections.testimonialsection === "On"}
                  onChange={(checked) =>
                    handleToggleChange(
                      "testimonialsection",
                      checked ? "On" : "Off"
                    )
                  }
                />

                {/* About Section */}
                <ToggleSetting
                  label="About Section"
                  description="Tell your story and company information"
                  checked={localSections.aboutsection === "On"}
                  onChange={(checked) =>
                    handleToggleChange("aboutsection", checked ? "On" : "Off")
                  }
                />

                {/* Gallery Section */}
                <ToggleSetting
                  label="Gallery Section"
                  description="Showcase your work with image gallery"
                  checked={localSections.gallerysection === "On"}
                  onChange={(checked) =>
                    handleToggleChange("gallerysection", checked ? "On" : "Off")
                  }
                />

                {/* Footer Settings */}
                {/* <ToggleSetting
                  label="Footer Settings"
                  description="Manage footer links and information"
                  checked={localSections.footersection === "On"}
                  onChange={(checked) =>
                    handleToggleChange("footersection", checked ? "On" : "Off")
                  }
                /> */}

                {/* Font & Theme */}
                {/* <ToggleSetting
                  label="Font & Theme"
                  description="Customize fonts and theme colors"
                  checked={localSections.fronttheme === "On"}
                  onChange={(checked) =>
                    handleToggleChange("fronttheme", checked ? "On" : "Off")
                  }
                /> */}

                {/* About Us Page */}
                {/* <ToggleSetting
                  label="About Us Page"
                  description="Dedicated page for about us content"
                  checked={localSections.aboutuspage === "On"}
                  onChange={(checked) =>
                    handleToggleChange("aboutuspage", checked ? "On" : "Off")
                  }
                /> */}
              </div>
            </div>

            {/* Error Display */}
            {Object.keys(errors).length > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs text-red-700 font-medium mb-1.5">
                  Errors occurred:
                </p>
                <ul className="list-disc list-inside text-xs text-red-600 space-y-0.5">
                  {Object.entries(errors).map(([key, message]) => (
                    <li key={key}>{message}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </WebsiteSectionCard>
      </div>
    </>
  );
};

export default SectionSettings;
